import {
  IMAGE_MODELS,
  MAX_TREND_FRAMES,
  MAX_VIDEO_FRAMES,
  RESOLUTIONS,
  SONG_MODELS,
  VIDEO_ASPECT_RATIOS,
  VIDEO_MODELS,
  VIDEO_RESOLUTIONS,
  TREND_ASPECT_RATIOS,
  TREND_SECTIONS,
  TREND_STATUS_LABELS,
  TREND_TAG_LABELS,
  framesOfStep,
  outputFrames,
  outputKind,
  placeholdersOf,
  variantsOf,
  MAX_STICKERS,
  publishIssues,
  trendEconomics,
  type AdminGenerationDetail,
  type AdminProvidersView,
  type AdminTrendDetail,
  type AdminTrendListItem,
  type ControlStep,
  type ImageStep,
  type SongStep,
  type StickerStep,
  type TrendDefinition,
  type TrendField,
  type TrendStep,
  type VideoStep,
} from '@trendator/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Icons } from '../components/icons';
import { Link } from '../components/Layout';
import {
  Button,
  Card,
  CardTitle,
  Chip,
  Dot,
  Empty,
  ErrorBox,
  Field,
  Input,
  Label,
  Loading,
  Modal,
  Select,
  Spinner,
  Status,
  Switch,
  Textarea,
  useToast,
  type Tone,
} from '../components/ui';
import { cn } from '../lib/cn';
import { api, errorText } from '../lib/api';
import { plural, rub, seconds } from '../lib/format';
import { useLoad, usePoll } from '../lib/hooks';
import { navigate } from '../lib/router';

const KIND_LABEL = { photo: 'фото', video: 'видео', song: 'песня', stickers: 'стикеры' } as const;
const KIND_FILTERS = [
  { id: 'all', label: 'Все' },
  { id: 'photo', label: 'Фото' },
  { id: 'video', label: 'Видео' },
  { id: 'song', label: 'Песни' },
  { id: 'stickers', label: 'Стикеры' },
] as const;

function statusTone(t: { status: string; dirty: boolean }): Tone {
  if (t.status === 'disabled') return 'idle';
  if (t.status === 'draft' || t.dirty) return 'warn';
  return 'ok';
}

const DEFAULT_DEFINITION: TrendDefinition = {
  inputs: { photos: { min: 1, max: 1 }, photoHint: 'Одно фото, где хорошо видно лицо', twin: true, fields: [], duo: false, minors: false },
  steps: [
    {
      id: 's1',
      type: 'image',
      title: '',
      model: 'nano-banana-2',
      resolution: '1K',
      aspectRatio: '9:16',
      refs: 'user',
      fanout: 'each',
      minSuccess: 0,
      variants: [{ label: '', prompt: 'Человек с фото — ... Сохранить лицо и причёску, кадр 9:16' }],
    },
  ],
};

export function Trends({ id }: { id: string | null }) {
  const list = useLoad(() => api.get<{ trends: AdminTrendListItem[] }>('/trends'));
  const toast = useToast();
  const [creating, setCreating] = useState(false);

  const create = async () => {
    setCreating(true);
    try {
      const slug = `trend-${Math.random().toString(36).slice(2, 7)}`;
      const t = await api.post<AdminTrendDetail>('/trends', { slug, title: 'Новый тренд', price: 0, definition: DEFAULT_DEFINITION });
      await list.reload();
      navigate(`/trends/${t.id}`);
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setCreating(false);
    }
  };

  const sidebar = (
    <TrendList items={list.data?.trends ?? null} error={list.error} activeId={id} onCreate={() => void create()} creating={creating} />
  );

  if (!id) {
    return (
      <>
        <h1 className="m-0 text-2xl font-extrabold tracking-tight lg:text-[30px]">Тренды</h1>
        <div className="flex flex-wrap items-start gap-5">
          <div className="w-full lg:w-[320px]">{sidebar}</div>
          <Card className="hidden min-h-60 flex-1 items-center justify-center text-sm text-muted lg:flex">Выбери тренд слева или создай новый</Card>
        </div>
      </>
    );
  }
  return <TrendEditor key={id} id={id} sidebar={sidebar} onChanged={() => void list.reload()} />;
}

function TrendList({
  items,
  error,
  activeId,
  onCreate,
  creating,
}: {
  items: AdminTrendListItem[] | null;
  error: string | null;
  activeId: string | null;
  onCreate: () => void;
  creating: boolean;
}) {
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<(typeof KIND_FILTERS)[number]['id']>('all');
  const shown = (items ?? []).filter(
    (t) => (kind === 'all' || t.kind === kind) && (!q || t.title.toLowerCase().includes(q.toLowerCase()) || t.slug.includes(q.toLowerCase())),
  );
  return (
    <Card className="flex flex-col gap-1.5 p-3.5">
      <label className="px-1 pt-1 pb-2">
        <span className="sr-only">Поиск тренда</span>
        <Input placeholder="Найти тренд" value={q} onChange={(e) => setQ(e.target.value)} className="h-10" />
      </label>
      <div className="flex flex-wrap gap-1.5 px-1 pb-1.5">
        {KIND_FILTERS.map((f) => {
          const n = (items ?? []).filter((t) => f.id === 'all' || t.kind === f.id).length;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setKind(f.id)}
              className={cn('inline-flex h-7 cursor-pointer items-center rounded-lg px-2.5 text-[13px]', kind === f.id ? 'bg-accent font-semibold text-on-accent' : 'bg-chip text-text2')}
            >
              {f.label} {n}
            </button>
          );
        })}
      </div>
      {error ? <span className="px-2 text-sm text-bad">{error}</span> : null}
      {!items && !error ? <Loading /> : null}
      {items && shown.length === 0 ? <Empty>{items.length ? 'Ничего не нашли' : 'Трендов пока нет'}</Empty> : null}
      {shown.map((t) => (
        <Link
          key={t.id}
          to={`/trends/${t.id}`}
          className={cn('flex items-center gap-3 rounded-[14px] p-2.5 text-white hover:bg-white/4 hover:text-white', t.id === activeId && 'bg-accent/10 hover:bg-accent/10')}
        >
          <Cover url={t.coverUrl} gradient={t.coverGradient} className="size-10 rounded-[11px]" />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-sm font-semibold">{t.title}</span>
            <span className="text-xs text-muted">
              {KIND_LABEL[t.kind]} · {t.publishedPrice !== null ? rub(t.publishedPrice) : t.price ? rub(t.price) : 'без цены'}
              {t.generations7d ? ` · ${t.generations7d} за 7 дн` : ''}
            </span>
          </span>
          <Dot tone={statusTone(t)} aria-label={TREND_STATUS_LABELS[t.status]} />
        </Link>
      ))}
      <Button variant="dashed" className="mt-2" onClick={onCreate} busy={creating}>
        + Новый тренд
      </Button>
    </Card>
  );
}

function Cover({ url, gradient, className, children }: { url: string | null; gradient: string; className?: string; children?: React.ReactNode }) {
  return (
    <span className={cn('relative shrink-0 overflow-hidden bg-cover bg-center', className)} style={{ background: url ? `center / cover no-repeat url("${url}")` : gradient }}>
      {children}
    </span>
  );
}

// ---------- Редактор ----------

type Form = Pick<
  AdminTrendDetail,
  'slug' | 'title' | 'subtitle' | 'description' | 'kind' | 'section' | 'tags' | 'price' | 'allowFree' | 'sortOrder' | 'coverGradient' | 'definition'
>;
const FORM_KEYS = ['slug', 'title', 'subtitle', 'description', 'kind', 'section', 'tags', 'price', 'allowFree', 'sortOrder', 'coverGradient', 'definition'] as const;
const pick = (t: AdminTrendDetail): Form => Object.fromEntries(FORM_KEYS.map((k) => [k, structuredClone(t[k])])) as Form;

type Selected = 'input' | 'output' | number;

function TrendEditor({ id, sidebar, onChanged }: { id: string; sidebar: React.ReactNode; onChanged: () => void }) {
  const toast = useToast();
  const d = useLoad(() => api.get<AdminTrendDetail>(`/trends/${id}`), [id]);
  const prov = useLoad(() => api.get<AdminProvidersView>('/providers'));
  const [form, setForm] = useState<Form | null>(null);
  const [sel, setSel] = useState<Selected>(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [testOpen, setTestOpen] = useState(false);

  // Пришла свежая версия с сервера (сохранение, обложка, публикация): несохранённые правки не теряем.
  const base = useRef<Form | null>(null);
  useEffect(() => {
    if (!d.data) return;
    const next = pick(d.data);
    setForm((cur) => {
      if (!cur || !base.current) return next;
      const prev = base.current;
      const kept = FORM_KEYS.filter((k) => JSON.stringify(cur[k]) !== JSON.stringify(prev[k]));
      return { ...next, ...Object.fromEntries(kept.map((k) => [k, cur[k]])) } as Form;
    });
    base.current = next;
  }, [d.data]);

  const changed = useMemo(() => {
    if (!form || !d.data) return [] as (keyof Form)[];
    const base = pick(d.data);
    return FORM_KEYS.filter((k) => JSON.stringify(form[k]) !== JSON.stringify(base[k]));
  }, [form, d.data]);

  if (d.error && !d.data) {
    return (
      <>
        <h1 className="m-0 text-2xl font-extrabold">Тренды</h1>
        <ErrorBox text={d.error} onRetry={() => void d.reload()} />
      </>
    );
  }
  if (!d.data || !form) return <Loading />;
  const t = d.data;
  const def = form.definition;

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const setDef = (fn: (d: TrendDefinition) => TrendDefinition) => setForm((f) => (f ? { ...f, definition: fn(structuredClone(f.definition)) } : f));

  const eco = prov.data
    ? trendEconomics(def, form.price, prov.data.economics, prov.data.modelCosts, prov.data.settings.order.find((p) => prov.data!.settings.enabled[p]) ?? 'kie')
    : t.economics;
  const issues = [...publishIssues(def), ...(form.price <= 0 ? [{ message: 'Цена должна быть больше нуля' }] : [])];

  const save = async (): Promise<boolean> => {
    if (!changed.length) return true;
    setBusy('save');
    try {
      const patch = Object.fromEntries(changed.map((k) => [k, form[k]]));
      d.setData(await api.patch<AdminTrendDetail>(`/trends/${id}`, patch));
      onChanged();
      toast('Сохранено');
      return true;
    } catch (err) {
      toast(errorText(err), 'bad');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const action = async (name: string, fn: () => Promise<AdminTrendDetail>, ok: string) => {
    setBusy(name);
    try {
      d.setData(await fn());
      onChanged();
      toast(ok);
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    if (!(await save())) return;
    await action('publish', () => api.post<AdminTrendDetail>(`/trends/${id}/publish`), 'Опубликовано — уже в каталоге');
  };

  const upload = (kind: 'cover' | 'before') => async (file: File) =>
    action(kind, () => api.upload<AdminTrendDetail>(`/trends/${id}/asset?kind=${kind}`, file), kind === 'cover' ? 'Обложка обновлена' : 'Фото «до» обновлено');

  const badge = (() => {
    if (t.status === 'disabled') return <Status tone="idle">Выключен</Status>;
    if (t.status === 'draft') return <Status tone="warn">Черновик{t.published ? '' : ' · не публиковался'}</Status>;
    if (t.dirty || changed.length) return <Status tone="warn">Опубликован · есть изменения</Status>;
    return <Status tone="ok">Опубликован · v{t.published?.version}</Status>;
  })();

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3.5">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label>
            <Link to="/trends" className="text-muted">
              Тренды
            </Link>{' '}
            / {KIND_LABEL[form.kind]}
          </Label>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="m-0 text-2xl font-extrabold tracking-tight lg:text-[30px]">{form.title || 'Без названия'}</h1>
            {badge}
          </div>
        </div>
        <div className="flex flex-wrap gap-2.5">
          <Button onClick={() => setTestOpen(true)} disabled={!!busy}>
            {Icons.play()}Тест-прогон
          </Button>
          {changed.length ? (
            <Button onClick={() => void save()} busy={busy === 'save'}>
              Сохранить
            </Button>
          ) : null}
          {t.status === 'published' && !t.dirty && !changed.length ? (
            <Button variant="danger" busy={busy === 'status'} onClick={() => void action('status', () => api.post(`/trends/${id}/status`, { status: 'disabled' }), 'Тренд выключен')}>
              Выключить
            </Button>
          ) : null}
          {t.status === 'disabled' && t.published && !t.dirty && !changed.length ? (
            <Button busy={busy === 'status'} onClick={() => void action('status', () => api.post(`/trends/${id}/status`, { status: 'published' }), 'Тренд снова в каталоге')}>
              Включить
            </Button>
          ) : null}
          {t.status === 'draft' || t.dirty || changed.length || (t.status === 'disabled' && !t.published) ? (
            <Button variant="accent" onClick={() => void publish()} busy={busy === 'publish'} disabled={issues.length > 0} title={issues.map((i) => i.message).join('\n')}>
              Опубликовать
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-5">
        <div className="hidden min-w-0 flex-[1_1_250px] xl:block">{sidebar}</div>

        <div className="flex min-w-0 flex-[3_1_420px] flex-col">
          <div className="flex items-center justify-between px-1 pb-3.5">
            <span className="text-lg font-bold">Цепочка шагов</span>
            <Label>юзер видит только результат</Label>
          </div>
          <Chain def={def} sel={sel} setSel={setSel} setDef={setDef} costs={eco.perStep} />
        </div>

        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-4">
          <Card className="flex flex-col gap-3.5">
            <span className="text-base font-bold">Цена и экономика</span>
            <Field label="Цена для юзера, ₽" hint={t.published && t.published.price !== form.price ? `В каталоге сейчас ${rub(t.published.price)} — новая цена после публикации` : undefined}>
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                value={form.price}
                onChange={(e) => set('price', Math.max(0, Math.round(Number(e.target.value) || 0)))}
                className="text-xl font-bold"
              />
            </Field>
            <div className="flex flex-col gap-2.5 text-sm">
              <Row k={`Себестоимость шагов${eco.perStep.length > 1 ? '' : ''}`} v={rub(eco.costRub)} />
              <Row k={`Запас на брак (${prov.data?.economics.defectReservePct ?? 5}%)`} v={rub(eco.reserveRub)} />
              <Row k={`Эквайринг (${prov.data?.economics.acquiringPct ?? 9}%)`} v={rub(eco.acquiringRub)} />
            </div>
            <div className={cn('flex items-center justify-between rounded-[14px] p-3.5', eco.marginRub > 0 ? 'bg-ok/10' : 'bg-bad/10')}>
              <span className="flex flex-col gap-0.5">
                <span className={cn('text-xs', eco.marginRub > 0 ? 'text-[#8FE8BF]' : 'text-bad')}>Маржа с генерации</span>
                <b className="text-[22px]">{rub(eco.marginRub)}</b>
              </span>
              <b className={cn('text-[22px]', eco.marginRub > 0 ? 'text-ok' : 'text-bad')}>{eco.marginPct}%</b>
            </div>
            <span className="text-xs text-dim">
              Себестоимость — по ценам провайдеров и курсу $ {prov.data ? `(${prov.data.economics.usdRub} ₽)` : ''} из раздела «Провайдеры».
            </span>
          </Card>

          <Card className="flex flex-col gap-3.5">
            <span className="text-base font-bold">В каталоге</span>
            <div className="flex gap-3.5">
              <Cover url={t.coverUrl} gradient={form.coverGradient} className="flex h-[170px] w-24 items-end rounded-2xl p-2">
                <span className="rounded-md bg-black/40 px-[7px] py-[3px] text-[11px] font-bold">{coverAspect(def)}</span>
              </Cover>
              <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                <Field label="Раздел">
                  <Select
                    value={form.section}
                    onChange={(v) => set('section', v)}
                    options={TREND_SECTIONS.map((s) => ({ value: s.id, label: s.label }))}
                    className="h-[38px] text-sm"
                  />
                </Field>
                <div className="flex flex-wrap gap-1.5">
                  {(Object.keys(TREND_TAG_LABELS) as (keyof typeof TREND_TAG_LABELS)[]).map((tag) => {
                    const on = form.tags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => set('tags', on ? form.tags.filter((x) => x !== tag) : [...form.tags, tag])}
                        className={cn('h-7 cursor-pointer rounded-lg px-2.5 text-[13px]', on ? 'bg-accent font-semibold text-on-accent' : 'bg-chip text-text2')}
                      >
                        {TREND_TAG_LABELS[tag]}
                      </button>
                    );
                  })}
                </div>
                <FilePick label={t.coverUrl ? 'Заменить обложку' : 'Загрузить обложку'} onFile={upload('cover')} busy={busy === 'cover'} />
                <FilePick label={t.beforeUrl ? 'Заменить фото «до»' : 'Фото «до» (для экрана тренда)'} onFile={upload('before')} busy={busy === 'before'} />
              </div>
            </div>
            <Field label="Градиент-заглушка обложки" hint="CSS, пока нет картинки">
              <Input value={form.coverGradient} onChange={(e) => set('coverGradient', e.target.value)} className="h-[38px] text-sm" />
            </Field>
            <div className="flex items-center justify-between gap-3">
              <span className="flex flex-col">
                <span className="text-sm font-semibold">Бесплатная первая</span>
                <span className="text-xs text-muted">новый юзер может сделать этот тренд бесплатно</span>
              </span>
              <Switch checked={form.allowFree} onChange={(v) => set('allowFree', v)} label="Бесплатная первая" />
            </div>
            <Field label="Порядок в каталоге" hint="меньше — выше">
              <Input type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', Math.max(0, Math.round(Number(e.target.value) || 0)))} className="h-[38px] text-sm" />
            </Field>
          </Card>

          <Card className="flex flex-col gap-3.5">
            <span className="text-base font-bold">Карточка</span>
            <Field label="Название">
              <Input value={form.title} maxLength={60} onChange={(e) => set('title', e.target.value)} />
            </Field>
            <Field label="Подпись на карточке" hint={`например «${outputFrames(def)} ${OUT_NOUN[outputKind(def)]}»`}>
              <Input value={form.subtitle} maxLength={40} onChange={(e) => set('subtitle', e.target.value)} />
            </Field>
            <Field label="Описание на экране тренда">
              <Textarea value={form.description} maxLength={500} onChange={(e) => set('description', e.target.value)} />
            </Field>
            <Field label="Адрес (slug)" hint="латиница, цифры, дефис — ссылка на тренд: t.me/бот/app?startapp=t_slug">
              <Input value={form.slug} onChange={(e) => set('slug', e.target.value.toLowerCase())} className="font-mono text-sm" />
            </Field>
          </Card>

          {issues.length ? (
            <Card className="flex flex-col gap-2 border-warn/30">
              <span className="text-sm font-bold text-warn">Пока нельзя опубликовать</span>
              {issues.map((i) => (
                <span key={i.message} className="text-[13px] text-text2">
                  · {i.message}
                </span>
              ))}
            </Card>
          ) : null}

          <LastTest t={t} onOpen={() => setTestOpen(true)} />
        </div>
      </div>

      <TestRun
        open={testOpen}
        onClose={() => setTestOpen(false)}
        trend={t}
        changed={changed.length > 0}
        onSave={save}
        onStarted={() => void d.reload()}
      />
    </>
  );
}

const Row = ({ k, v }: { k: string; v: string }) => (
  <span className="flex justify-between">
    <span className="text-soft">{k}</span>
    <span>{v}</span>
  </span>
);

function FilePick({ label, onFile, busy }: { label: string; onFile: (f: File) => void; busy?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" onClick={() => ref.current?.click()} className="cursor-pointer text-left text-[13px] font-semibold text-accent hover:text-accent-hover" disabled={busy}>
        {busy ? <Spinner className="mr-1 size-3 align-middle" /> : null}
        {label}
      </button>
      <input
        ref={ref}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
    </>
  );
}

// ---------- Цепочка ----------

const REFS = [
  { value: 'user', label: 'Фото юзера' },
  { value: 'previous', label: 'Кадры прошлого шага' },
  { value: 'none', label: 'Без фото' },
] as const;
const VIDEO_REFS = [
  { value: 'previous', label: 'Оживить кадр прошлого шага' },
  { value: 'user', label: 'Фото юзера как референс' },
  { value: 'none', label: 'Без фото (по тексту)' },
] as const;
const FANOUT = [
  { value: 'each', label: 'Отдельно для каждого кадра' },
  { value: 'all', label: 'Все кадры одним запросом' },
] as const;
const RES_LABEL: Record<string, string> = { '1K': '1K · быстро', '2K': '2K', '4K': '4K · максимум', '480p': '480p · дёшево', '720p': '720p', '1080p': '1080p · дорого' };
const TYPE_LABEL = { image: 'КАРТИНКА', video: 'ВИДЕО', song: 'ПЕСНЯ', control: 'КОНТРОЛЬ', sticker: 'СТИКЕРЫ' } as const;
const OUT_NOUN = { image: 'фото', video: 'видео', song: 'песни', stickers: 'стикеров' } as const;

function coverAspect(def: TrendDefinition): string {
  const s = def.steps.find((x) => x.type === 'image' || x.type === 'video');
  return s && (s.type === 'image' || s.type === 'video') ? s.aspectRatio : def.steps.some((x) => x.type === 'song') ? '1:1' : '9:16';
}

function stepTitle(s: TrendStep): string {
  if (s.title) return s.title;
  if (s.type === 'image') return IMAGE_MODELS.find((m) => m.id === s.model)?.label ?? s.model;
  if (s.type === 'video') return VIDEO_MODELS.find((m) => m.id === s.model)?.label ?? s.model;
  if (s.type === 'song') return SONG_MODELS.find((m) => m.id === s.model)?.label ?? s.model;
  if (s.type === 'sticker') return 'Стикерпак в Telegram';
  return 'Проверка качества';
}

function stepChips(def: TrendDefinition, s: TrendStep, i: number): string[] {
  const frames = framesOfStep(def, i);
  const many = (n: number) => (n > 1 ? [`${n} ${plural(n, 'вариант', 'варианта', 'вариантов')}`] : []);
  if (s.type === 'image') {
    return [
      `${frames} ${plural(frames, 'кадр', 'кадра', 'кадров')}`,
      ...many(s.variants.length),
      s.aspectRatio,
      s.resolution,
      REFS.find((r) => r.value === s.refs)!.label.toLowerCase(),
      ...(s.minSuccess ? [`успех от ${s.minSuccess}`] : []),
    ];
  }
  if (s.type === 'video') {
    return [
      `${frames} видео`,
      ...many(s.variants.length),
      `${s.durationSec} с`,
      s.aspectRatio,
      s.resolution,
      s.generateAudio ? 'со звуком' : 'без звука',
      VIDEO_REFS.find((r) => r.value === s.refs)!.label.toLowerCase(),
    ];
  }
  if (s.type === 'song') return [`${frames} ${plural(frames, 'песня', 'песни', 'песен')}`, ...(s.style ? [s.style] : []), ...(s.instrumental ? ['без слов'] : [])];
  if (s.type === 'sticker') return [`${frames} ${plural(frames, 'стикер', 'стикера', 'стикеров')}`, 'фон — вырезаем', '512 px', `«${s.packTitle}»`];
  return [
    s.faceMatch > 0 ? `сходство лица ≥ ${s.faceMatch}` : 'без сверки лица',
    s.vision ? 'брак — vision-модель' : 'без vision',
    `переделать до ${s.maxRegenerations} раз`,
  ];
}

function newStep(type: TrendStep['type'], id: string, hasPrev: boolean): TrendStep {
  if (type === 'video') {
    return {
      id,
      type: 'video',
      title: '',
      model: 'seedance-2',
      resolution: '720p',
      aspectRatio: '9:16',
      durationSec: 5,
      generateAudio: true,
      refs: hasPrev ? 'previous' : 'user',
      fanout: 'each',
      minSuccess: 0,
      variants: [{ label: '', prompt: 'Камера медленно наезжает, лёгкий ветер, живая мимика. Лицо не меняется' }],
    };
  }
  if (type === 'song') {
    return { id, type: 'song', title: '', model: 'suno-v6', style: 'поп, бодро', instrumental: false, minSuccess: 0, variants: [{ label: '', prompt: 'Весёлая песня про {{name}}' }] };
  }
  if (type === 'control') return { id, type: 'control', title: '', faceMatch: 0.45, vision: true, maxRegenerations: 2 };
  if (type === 'sticker') return { id, type: 'sticker', title: '', packTitle: '{{name}} · стикеры', minSuccess: 0 };
  return {
    id,
    type: 'image',
    title: '',
    model: 'nano-banana-2',
    resolution: hasPrev ? '2K' : '1K',
    aspectRatio: '9:16',
    refs: hasPrev ? 'previous' : 'user',
    fanout: 'each',
    minSuccess: 0,
    variants: [{ label: '', prompt: hasPrev ? 'Улучши детали и резкость, ничего не меняя в лице' : 'Человек с фото — ...' }],
  };
}

type SetDef = (fn: (d: TrendDefinition) => TrendDefinition) => void;

function Chain({
  def,
  sel,
  setSel,
  setDef,
  costs,
}: {
  def: TrendDefinition;
  sel: Selected;
  setSel: (s: Selected) => void;
  setDef: SetDef;
  costs: { id: string; costRub: number }[];
}) {
  const photos = def.inputs.photos;
  const out = outputFrames(def);
  const kind = outputKind(def);
  const [adding, setAdding] = useState(false);
  const nextId = () => {
    let n = def.steps.length + 1;
    while (def.steps.some((s) => s.id === `s${n}`)) n += 1;
    return `s${n}`;
  };
  const addStep = (type: TrendStep['type']) => {
    const step = newStep(type, nextId(), def.steps.some((s) => s.type === 'image'));
    setDef((d) => {
      d.steps.push(step);
      return d;
    });
    setSel(def.steps.length);
    setAdding(false);
  };

  const items: { key: string; sel: Selected; n: number; type: string; title: string; cost: string; prompt?: string; chips: string[] }[] = [
    {
      key: 'input',
      sel: 'input',
      n: 1,
      type: 'ВХОД',
      title: `${def.inputs.twin ? 'Фото юзера или двойник' : 'Фото юзера'}${def.inputs.duo ? ' + друг' : ''}`,
      cost: '0 ₽',
      chips: [
        photos.max === 0 ? 'без фото' : photos.min === photos.max ? `${photos.min} фото` : `${photos.min}–${photos.max} фото`,
        ...(def.inputs.twin ? ['двойник'] : []),
        ...(def.inputs.duo ? ['дуо: селфи друга'] : []),
        ...def.inputs.fields.map((f) => `{{${f.id}}} ${f.label}`),
        def.inputs.photoHint,
        def.inputs.minors ? 'можно детей' : 'только 18+',
      ],
    },
    ...def.steps.map((s, i) => {
      const cost = costs.find((c) => c.id === s.id)?.costRub ?? 0;
      return {
        key: s.id,
        sel: i as Selected,
        n: i + 2,
        type: TYPE_LABEL[s.type],
        title: stepTitle(s),
        cost: `${cost.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ₽`,
        prompt: variantsOf(s).length === 1 ? variantsOf(s)[0]!.prompt : undefined,
        chips: stepChips(def, s, i),
      };
    }),
    {
      key: 'output',
      sel: 'output',
      n: def.steps.length + 2,
      type: 'ВЫДАЧА',
      title: 'Результат юзеру',
      cost: '0 ₽',
      chips: [`${out} ${OUT_NOUN[kind]}`, 'бот пишет «Готово!»'],
    },
  ];

  return (
    <>
      {items.map((it, idx) => {
        const on = sel === it.sel;
        const stepIndex = typeof it.sel === 'number' ? it.sel : -1;
        const step = stepIndex >= 0 ? def.steps[stepIndex] : undefined;
        return (
          <div key={it.key} className="flex flex-col">
            <div
              className={cn(
                'flex flex-col gap-3 rounded-[20px] border bg-card p-[18px]',
                on ? 'border-accent shadow-[0_0_0_4px_rgba(45,180,255,0.12)]' : 'cursor-pointer border-line hover:border-white/15',
              )}
              onClick={() => !on && setSel(it.sel)}
            >
              <div className="flex items-center gap-3">
                <span className={cn('flex size-8 shrink-0 items-center justify-center rounded-[10px] text-sm font-extrabold', on ? 'bg-accent text-on-accent' : 'bg-chip text-text2')}>{it.n}</span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="text-xs font-semibold tracking-[0.04em] text-muted">{it.type}</span>
                  <span className="truncate text-base font-bold">{it.title}</span>
                </span>
                {step ? (
                  <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <IconBtn label="Выше" disabled={stepIndex === 0} onClick={() => { setDef((d) => { const [x] = d.steps.splice(stepIndex, 1); d.steps.splice(stepIndex - 1, 0, x!); return d; }); setSel(stepIndex - 1); }}>{Icons.up()}</IconBtn>
                    <IconBtn label="Ниже" disabled={stepIndex === def.steps.length - 1} onClick={() => { setDef((d) => { const [x] = d.steps.splice(stepIndex, 1); d.steps.splice(stepIndex + 1, 0, x!); return d; }); setSel(stepIndex + 1); }}>{Icons.down()}</IconBtn>
                    <IconBtn label="Удалить шаг" disabled={def.steps.length === 1} onClick={() => { if (confirm('Удалить шаг?')) { setDef((d) => { d.steps.splice(stepIndex, 1); return d; }); setSel('input'); } }}>{Icons.trash()}</IconBtn>
                  </span>
                ) : null}
                <span className="text-sm font-semibold whitespace-nowrap text-text2">{it.cost}</span>
              </div>
              {!on && it.prompt ? (
                <div className="line-clamp-3 rounded-xl border border-line bg-field px-3.5 py-3 text-sm leading-normal text-text2">{it.prompt}</div>
              ) : null}
              {!on ? (
                <div className="flex flex-wrap gap-1.5">
                  {it.chips.map((c) => (
                    <Chip key={c}>{c}</Chip>
                  ))}
                </div>
              ) : null}
              {on && it.sel === 'input' ? <InputEditor def={def} setDef={setDef} /> : null}
              {on && step?.type === 'image' ? <ImageStepEditor step={step} index={stepIndex} setDef={setDef} /> : null}
              {on && step?.type === 'video' ? <VideoStepEditor step={step} index={stepIndex} setDef={setDef} /> : null}
              {on && step?.type === 'song' ? <SongStepEditor step={step} index={stepIndex} setDef={setDef} /> : null}
              {on && step?.type === 'control' ? <ControlStepEditor step={step} index={stepIndex} setDef={setDef} /> : null}
              {on && step?.type === 'sticker' ? <StickerStepEditor step={step} index={stepIndex} setDef={setDef} /> : null}
              {on && it.sel === 'output' ? (
                <span className="text-sm text-soft">
                  {kind === 'stickers'
                    ? `Юзер получит стикерпак из ${out} ${plural(out, 'стикера', 'стикеров', 'стикеров')} — бот соберёт его в Telegram, ссылка t.me/addstickers/… придёт в бот.`
                    : kind === 'song'
                    ? `Юзер получит ${out} ${plural(out, 'песню', 'песни', 'песен')} с обложкой и текстом — плеер в мини-аппе и аудио в боте.`
                    : kind === 'video'
                      ? `Юзер получит ${out} видео с обложкой и версиями 9:16 / 1:1 / 16:9 — в мини-аппе и в боте.`
                      : `Юзер получит ${out} ${plural(out, 'кадр', 'кадра', 'кадров')} последнего шага с подписями вариантов — в мини-аппе и альбомом в боте.`}
                </span>
              ) : null}
            </div>
            {idx < items.length - 1 ? <div className="ml-[33px] h-5 w-0.5 bg-accent/40" /> : null}
          </div>
        );
      })}
      {adding ? (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5" role="group" aria-label="Тип шага">
          {(
            [
              ['image', 'Картинка'],
              ['video', 'Видео'],
              ['song', 'Песня'],
              ['control', 'Контроль'],
              ['sticker', 'Стикеры'],
            ] as const
          ).map(([type, label]) => (
            <Button key={type} variant="dashed" className="h-[52px] rounded-[18px]" onClick={() => addStep(type)}>
              + {label}
            </Button>
          ))}
        </div>
      ) : (
        <Button variant="dashed" className="mt-4 h-[52px] rounded-[18px]" onClick={() => setAdding(true)} disabled={def.steps.length >= 8}>
          + Добавить шаг
        </Button>
      )}
    </>
  );
}

function IconBtn({ label, children, ...p }: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button type="button" aria-label={label} title={label} {...p} className="flex size-7 cursor-pointer items-center justify-center rounded-lg text-muted hover:bg-white/6 hover:text-white disabled:cursor-default disabled:opacity-30">
      {children}
    </button>
  );
}

function InputEditor({ def, setDef }: { def: TrendDefinition; setDef: SetDef }) {
  const p = def.inputs.photos;
  const n = (v: string) => Math.max(0, Math.min(4, Math.round(Number(v) || 0)));
  const setInputs = (patch: Partial<TrendDefinition['inputs']>) => setDef((d) => ({ ...d, inputs: { ...d.inputs, ...patch } }));
  const setField = (i: number, patch: Partial<TrendField>) => setInputs({ fields: def.inputs.fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  const used = new Set(
    def.steps
      .flatMap((s) => [...variantsOf(s).flatMap((v) => [v.prompt, v.label]), s.type === 'song' ? s.style : '', s.type === 'sticker' ? s.packTitle : ''])
      .flatMap(placeholdersOf),
  );
  return (
    <div className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Фото минимум">
          <Input type="number" min={0} max={4} value={p.min} onChange={(e) => setInputs({ photos: { ...p, min: n(e.target.value) } })} />
        </Field>
        <Field label="Фото максимум">
          <Input type="number" min={0} max={4} value={p.max} onChange={(e) => setInputs({ photos: { ...p, max: n(e.target.value) } })} />
        </Field>
        <Field label="Подсказка юзеру" className="col-span-2">
          <Input value={def.inputs.photoHint} maxLength={120} onChange={(e) => setInputs({ photoHint: e.target.value })} />
        </Field>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-sm font-semibold">Можно с двойником</span>
          <span className="text-xs text-muted">вместо фото — лучшие ракурсы готового двойника юзера</span>
        </span>
        <Switch checked={def.inputs.twin} onChange={(v) => setInputs({ twin: v })} label="Можно с двойником" />
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-sm font-semibold">Вдвоём с другом (дуо)</span>
          <span className="text-xs text-muted">друг делает селфи и соглашается в мини-аппе; его фото идёт последним референсом в шаги «фото юзера»</span>
        </span>
        <Switch checked={def.inputs.duo} onChange={(v) => setInputs({ duo: v })} label="Вдвоём с другом" />
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-sm font-semibold">Можно детей</span>
          <span className="text-xs text-muted">для раздела «Дети, питомцы»: принимаем фото, где лицо младше 18. Иначе такое фото отклоняем</span>
        </span>
        <Switch checked={def.inputs.minors} onChange={(v) => setInputs({ minors: v })} label="Можно детей" />
      </div>

      <div className="flex items-center justify-between pt-1">
        <span className="text-sm font-bold">Поля для юзера</span>
        <Label>в промптах — {'{{id}}'}</Label>
      </div>
      {def.inputs.fields.map((f, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-end gap-2 rounded-2xl border border-line bg-field/60 p-3">
          <Field label="id">
            <Input value={f.id} maxLength={20} onChange={(e) => setField(i, { id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} className="h-9 font-mono text-sm" />
          </Field>
          <Field label="Подпись">
            <Input value={f.label} maxLength={40} onChange={(e) => setField(i, { label: e.target.value })} className="h-9 text-sm" />
          </Field>
          <IconBtn label="Удалить поле" onClick={() => setInputs({ fields: def.inputs.fields.filter((_, j) => j !== i) })}>
            {Icons.trash()}
          </IconBtn>
          <Field label="Пример (placeholder)">
            <Input value={f.placeholder} maxLength={80} onChange={(e) => setField(i, { placeholder: e.target.value })} className="h-9 text-sm" />
          </Field>
          <Field label="Макс. символов">
            <Input type="number" min={1} max={300} value={f.maxLength} onChange={(e) => setField(i, { maxLength: Math.max(1, Math.min(300, Math.round(Number(e.target.value) || 1))) })} className="h-9 text-sm" />
          </Field>
          <span className="flex h-9 items-center">
            <Switch checked={f.required} onChange={(v) => setField(i, { required: v })} label="Обязательное" />
          </span>
          {!used.has(f.id) ? <span className="col-span-3 text-xs text-warn">{`{{${f.id}}}`} нигде не используется</span> : null}
        </div>
      ))}
      <Button
        size="sm"
        variant="dashed"
        disabled={def.inputs.fields.length >= 5}
        onClick={() => {
          let k = def.inputs.fields.length + 1;
          const ids = new Set(def.inputs.fields.map((f) => f.id));
          const id = ids.has('name') ? (() => { while (ids.has(`field${k}`)) k += 1; return `field${k}`; })() : 'name';
          setInputs({ fields: [...def.inputs.fields, { id, label: id === 'name' ? 'Имя' : 'Поле', placeholder: '', maxLength: 60, required: true }] });
        }}
      >
        + Поле
      </Button>
    </div>
  );
}

function useStepUpdate<S extends TrendStep>(index: number, setDef: SetDef) {
  return (patch: Partial<S>) =>
    setDef((d) => {
      d.steps[index] = { ...(d.steps[index] as S), ...patch };
      return d;
    });
}

function Variants({
  variants,
  max,
  onChange,
  title = 'Варианты · каждый = кадр',
  labelHint = 'Подпись кадра, напр. «Рыцарь»',
  promptHint = 'Промпт',
}: {
  variants: { label: string; prompt: string }[];
  max: number;
  onChange: (v: { label: string; prompt: string }[]) => void;
  title?: string;
  labelHint?: string;
  promptHint?: string;
}) {
  const setVariant = (i: number, patch: Partial<{ label: string; prompt: string }>) => onChange(variants.map((v, j) => (j === i ? { ...v, ...patch } : v)));
  return (
    <>
      <div className="flex items-center justify-between pt-1">
        <span className="text-sm font-bold">{title}</span>
        <Label>
          {variants.length} / {max}
        </Label>
      </div>
      {variants.map((v, i) => (
        <div key={i} className="flex flex-col gap-2 rounded-2xl border border-line bg-field/60 p-3">
          <div className="flex items-center gap-2">
            <Input value={v.label} maxLength={30} placeholder={labelHint} onChange={(e) => setVariant(i, { label: e.target.value })} className="h-9 text-sm" />
            <IconBtn label="Удалить вариант" disabled={variants.length === 1} onClick={() => onChange(variants.filter((_, j) => j !== i))}>
              {Icons.trash()}
            </IconBtn>
          </div>
          <Textarea value={v.prompt} maxLength={4000} placeholder={promptHint} onChange={(e) => setVariant(i, { prompt: e.target.value })} className="min-h-20 text-sm" />
        </div>
      ))}
      <Button size="sm" variant="dashed" disabled={variants.length >= max} onClick={() => onChange([...variants, { label: '', prompt: variants.at(-1)?.prompt ?? '' }])}>
        + Вариант
      </Button>
    </>
  );
}

const MinSuccess = ({ value, onChange }: { value: number; onChange: (n: number) => void }) => (
  <Field label="Минимум удачных кадров" hint="меньше — провал и полный возврат; 0 — нужны все">
    <Input type="number" min={0} max={MAX_TREND_FRAMES} value={value} onChange={(e) => onChange(Math.max(0, Math.min(MAX_TREND_FRAMES, Math.round(Number(e.target.value) || 0))))} />
  </Field>
);

function ImageStepEditor({ step, index, setDef }: { step: ImageStep; index: number; setDef: SetDef }) {
  const upd = useStepUpdate<ImageStep>(index, setDef);
  return (
    <div className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Название шага">
          <Input value={step.title} maxLength={60} placeholder={stepTitle({ ...step, title: '' })} onChange={(e) => upd({ title: e.target.value })} />
        </Field>
        <Field label="Модель">
          <Select value={step.model} onChange={(v) => upd({ model: v })} options={IMAGE_MODELS.map((m) => ({ value: m.id, label: m.label }))} />
        </Field>
        <Field label="Качество">
          <Select value={step.resolution} onChange={(v) => upd({ resolution: v })} options={RESOLUTIONS.map((r) => ({ value: r, label: RES_LABEL[r]! }))} />
        </Field>
        <Field label="Формат кадра">
          <Select value={step.aspectRatio} onChange={(v) => upd({ aspectRatio: v })} options={TREND_ASPECT_RATIOS.map((r) => ({ value: r, label: r }))} />
        </Field>
        <Field label="Откуда фото">
          <Select value={step.refs} onChange={(v) => upd({ refs: v })} options={REFS.map((r) => ({ ...r, disabled: index === 0 && r.value === 'previous' }))} />
        </Field>
        {step.refs === 'previous' ? (
          <Field label="Кадры прошлого шага">
            <Select value={step.fanout} onChange={(v) => upd({ fanout: v })} options={FANOUT} />
          </Field>
        ) : (
          <div className="hidden sm:block" />
        )}
        <MinSuccess value={step.minSuccess} onChange={(n) => upd({ minSuccess: n })} />
      </div>
      <Variants variants={step.variants} max={MAX_TREND_FRAMES} onChange={(variants) => upd({ variants })} />
    </div>
  );
}

function VideoStepEditor({ step, index, setDef }: { step: VideoStep; index: number; setDef: SetDef }) {
  const upd = useStepUpdate<VideoStep>(index, setDef);
  return (
    <div className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Название шага">
          <Input value={step.title} maxLength={60} placeholder={stepTitle({ ...step, title: '' })} onChange={(e) => upd({ title: e.target.value })} />
        </Field>
        <Field label="Модель">
          <Select value={step.model} onChange={(v) => upd({ model: v })} options={VIDEO_MODELS.map((m) => ({ value: m.id, label: m.label }))} />
        </Field>
        <Field label="Качество" hint="цена растёт с качеством и длиной">
          <Select value={step.resolution} onChange={(v) => upd({ resolution: v })} options={VIDEO_RESOLUTIONS.map((r) => ({ value: r, label: RES_LABEL[r]! }))} />
        </Field>
        <Field label="Формат">
          <Select value={step.aspectRatio} onChange={(v) => upd({ aspectRatio: v })} options={VIDEO_ASPECT_RATIOS.map((r) => ({ value: r, label: r }))} />
        </Field>
        <Field label="Длина, секунд" hint="4–15">
          <Input type="number" min={4} max={15} value={step.durationSec} onChange={(e) => upd({ durationSec: Math.max(4, Math.min(15, Math.round(Number(e.target.value) || 5))) })} />
        </Field>
        <Field label="Откуда фото">
          <Select value={step.refs} onChange={(v) => upd({ refs: v })} options={VIDEO_REFS.map((r) => ({ ...r, disabled: index === 0 && r.value === 'previous' }))} />
        </Field>
        {step.refs === 'previous' ? (
          <Field label="Кадры прошлого шага">
            <Select value={step.fanout} onChange={(v) => upd({ fanout: v })} options={[{ value: 'each', label: 'Видео из каждого кадра' }]} />
          </Field>
        ) : null}
        <MinSuccess value={step.minSuccess} onChange={(n) => upd({ minSuccess: n })} />
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-sm font-semibold">Звук от модели</span>
          <span className="text-xs text-muted">атмосфера, эффекты</span>
        </span>
        <Switch checked={step.generateAudio} onChange={(v) => upd({ generateAudio: v })} label="Звук от модели" />
      </div>
      <Variants variants={step.variants} max={MAX_VIDEO_FRAMES} onChange={(variants) => upd({ variants })} title="Варианты · каждый = видео" promptHint="Что происходит в кадре: движение камеры, действие" />
    </div>
  );
}

function SongStepEditor({ step, index, setDef }: { step: SongStep; index: number; setDef: SetDef }) {
  const upd = useStepUpdate<SongStep>(index, setDef);
  return (
    <div className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Название шага">
          <Input value={step.title} maxLength={60} placeholder={stepTitle({ ...step, title: '' })} onChange={(e) => upd({ title: e.target.value })} />
        </Field>
        <Field label="Модель">
          <Select value={step.model} onChange={(v) => upd({ model: v })} options={SONG_MODELS.map((m) => ({ value: m.id, label: m.label }))} />
        </Field>
        <Field label="Стиль" hint="жанр, настроение, вокал; можно {{поля}}" className="sm:col-span-2">
          <Input value={step.style} maxLength={300} placeholder="поп-панк, бодро, мужской вокал" onChange={(e) => upd({ style: e.target.value })} />
        </Field>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-sm font-semibold">Без слов</span>
          <span className="text-xs text-muted">только музыка</span>
        </span>
        <Switch checked={step.instrumental} onChange={(v) => upd({ instrumental: v })} label="Без слов" />
      </div>
      <Variants variants={step.variants} max={4} onChange={(variants) => upd({ variants })} title="Варианты · каждый = песня" promptHint="О чём песня — текст Suno напишет сам" />
      <span className="text-xs text-dim">Suno обычно отдаёт 2 трека на запрос — юзер получит все.</span>
    </div>
  );
}

function StickerStepEditor({ step, index, setDef }: { step: StickerStep; index: number; setDef: SetDef }) {
  const upd = useStepUpdate<StickerStep>(index, setDef);
  return (
    <div className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
      <span className="text-sm text-soft">
        Берёт кадры шага-картинки над ним, вырезает фон (kie.ai recraft/remove-background, запасной — fal BiRefNet), ужимает до 512 px и собирает пак в
        Telegram от имени юзера. Эмодзи — из подписи варианта («😂 Смех»), нет эмодзи — 🙂. До {MAX_STICKERS} стикеров.
      </span>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Название пака" hint="видно в Telegram; можно {{поля}}" className="sm:col-span-2">
          <Input value={step.packTitle} maxLength={64} onChange={(e) => upd({ packTitle: e.target.value })} />
        </Field>
        <MinSuccess value={step.minSuccess} onChange={(n) => upd({ minSuccess: n })} />
      </div>
    </div>
  );
}

function ControlStepEditor({ step, index, setDef }: { step: ControlStep; index: number; setDef: SetDef }) {
  const upd = useStepUpdate<ControlStep>(index, setDef);
  return (
    <div className="flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
      <span className="text-sm text-soft">Проверяет кадры шага сразу над ним. Брак переделываем за наш счёт; если не вышло — кадр не засчитан (возврат по порогу шага).</span>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Сходство лица, порог" hint="0 — не сверять; один человек обычно ≥ 0,4">
          <Input type="number" min={0} max={1} step={0.05} value={step.faceMatch} onChange={(e) => upd({ faceMatch: Math.max(0, Math.min(1, Number(e.target.value) || 0)) })} />
        </Field>
        <Field label="Переделать брак, раз" hint="0–3, за наш счёт">
          <Input type="number" min={0} max={3} value={step.maxRegenerations} onChange={(e) => upd({ maxRegenerations: Math.max(0, Math.min(3, Math.round(Number(e.target.value) || 0))) })} />
        </Field>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex flex-col">
          <span className="text-sm font-semibold">Брак глазами модели</span>
          <span className="text-xs text-muted">лишние пальцы, поплывшее лицо, мимо промпта</span>
        </span>
        <Switch checked={step.vision} onChange={(v) => upd({ vision: v })} label="Брак глазами модели" />
      </div>
    </div>
  );
}

// ---------- Тест-прогон ----------

function LastTest({ t, onOpen }: { t: AdminTrendDetail; onOpen: () => void }) {
  const lt = t.lastTest;
  const tone: Tone = !lt ? 'idle' : lt.status === 'succeeded' ? 'ok' : lt.status === 'failed' ? 'bad' : 'run';
  const label = !lt
    ? 'Ещё не запускали'
    : lt.status === 'succeeded'
      ? `Прошёл · ${seconds(lt.durationSec)}`
      : lt.status === 'failed'
        ? `Не прошёл · ${seconds(lt.durationSec)}`
        : `Идёт · ${lt.framesDone} из ${lt.frames}`;
  return (
    <Card className="flex flex-col gap-2.5">
      <CardTitle>Последний тест</CardTitle>
      <span className="flex items-center gap-2 text-sm">
        <Dot tone={tone} />
        {label}
        {lt ? <span className="text-muted">· {lt.draft ? 'черновик' : 'опубликованная'}</span> : null}
      </span>
      {lt ? (
        <Link to={`/generations?id=${lt.generationId}`} className="text-[13px] font-semibold">
          Открыть генерацию →
        </Link>
      ) : (
        <button type="button" onClick={onOpen} className="cursor-pointer text-left text-[13px] font-semibold text-accent">
          Запустить тест-прогон
        </button>
      )}
      <Label>тестовые прогоны идут за счёт проекта и в статистику не попадают</Label>
    </Card>
  );
}

function TestRun({
  open,
  onClose,
  trend,
  changed,
  onSave,
  onStarted,
}: {
  open: boolean;
  onClose: () => void;
  trend: AdminTrendDetail;
  changed: boolean;
  onSave: () => Promise<boolean>;
  onStarted: () => void;
}) {
  const toast = useToast();
  const [photos, setPhotos] = useState<{ id: string; url: string }[]>([]);
  const [useTwin, setUseTwin] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [use, setUse] = useState<'draft' | 'published'>('draft');
  const [busy, setBusy] = useState(false);
  const [genId, setGenId] = useState<string | null>(null);
  const [gen, setGen] = useState<AdminGenerationDetail | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const duoExtra = trend.definition.inputs.duo ? 1 : 0;
  const need = { min: trend.definition.inputs.photos.min + duoExtra, max: trend.definition.inputs.photos.max + duoExtra };

  const poll = async () => {
    if (!genId) return;
    try {
      setGen(await api.get<AdminGenerationDetail>(`/generations/${genId}`));
    } catch {
      /* следующий опрос */
    }
  };
  usePoll(() => void poll(), 2500, open && !!genId && (!gen || gen.status === 'queued' || gen.status === 'running' || gen.status === 'stuck'));
  useEffect(() => {
    if (gen && (gen.status === 'succeeded' || gen.status === 'failed')) onStarted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gen?.status]);

  const addPhoto = async (f: File) => {
    setBusy(true);
    try {
      const up = await api.upload<{ id: string; url: string }>('/uploads', f);
      setPhotos((p) => [...p, up].slice(-Math.max(1, need.max)));
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    if (use === 'draft' && changed && !(await onSave())) return;
    setBusy(true);
    try {
      const r = await api.post<{ generationId: string }>(`/trends/${trend.id}/test-run`, {
        photoIds: useTwin ? [] : photos.map((p) => p.id),
        useTwin,
        fields,
        use,
      });
      setGen(null);
      setGenId(r.generationId);
      onStarted();
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setGenId(null);
    setGen(null);
  };

  return (
    <Modal open={open} onClose={onClose} title="Тест-прогон">
      {!genId ? (
        <>
          <span className="text-sm text-soft">
            Прогон за счёт проекта, в статистику не идёт. Результат придёт и сюда, и тебе в бот.
            {changed ? ' Несохранённые правки сохраним перед запуском.' : ''}
          </span>
          {trend.published ? (
            <div className="flex gap-2">
              {(['draft', 'published'] as const).map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setUse(u)}
                  className={cn('h-9 flex-1 cursor-pointer rounded-[10px] border text-sm font-semibold', use === u ? 'border-accent bg-accent text-on-accent' : 'border-white/8 text-text2')}
                >
                  {u === 'draft' ? 'Черновик' : `Опубликованная v${trend.published!.version}`}
                </button>
              ))}
            </div>
          ) : null}
          {trend.definition.inputs.twin && !trend.definition.inputs.duo ? (
            <div className="flex items-center justify-between gap-3">
              <span className="flex flex-col">
                <span className="text-sm font-semibold">С моим двойником</span>
                <span className="text-xs text-muted">двойник твоего Telegram-аккаунта, создаётся в мини-аппе</span>
              </span>
              <Switch checked={useTwin} onChange={setUseTwin} label="С моим двойником" />
            </div>
          ) : null}
          {trend.definition.inputs.fields.map((f) => (
            <Field key={f.id} label={`${f.label}${f.required ? '' : ' · необязательно'}`}>
              <Input value={fields[f.id] ?? ''} maxLength={f.maxLength} placeholder={f.placeholder} onChange={(e) => setFields((v) => ({ ...v, [f.id]: e.target.value }))} />
            </Field>
          ))}
          {need.max > 0 && !useTwin ? (
            <div className="flex flex-col gap-2">
              <Label>
                Фото: нужно {need.min === need.max ? need.min : `${need.min}–${need.max}`} · {trend.definition.inputs.photoHint}
                {duoExtra ? ' · дуо: последним — фото «друга»' : ''}
              </Label>
              <div className="flex flex-wrap gap-2">
                {photos.map((p) => (
                  <img key={p.id} src={p.url} alt="" className="size-20 rounded-xl object-cover" />
                ))}
                {photos.length < need.max ? (
                  <button type="button" onClick={() => fileRef.current?.click()} className="flex size-20 cursor-pointer items-center justify-center rounded-xl border border-dashed border-accent/40 text-2xl text-accent" aria-label="Добавить фото">
                    {busy ? <Spinner /> : '+'}
                  </button>
                ) : null}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void addPhoto(f);
                  e.target.value = '';
                }}
              />
            </div>
          ) : null}
          <Button variant="accent" busy={busy} disabled={(!useTwin && photos.length < need.min) || trend.definition.inputs.fields.some((f) => f.required && !(fields[f.id] ?? '').trim())} onClick={() => void run()}>
            {Icons.play()}Запустить
          </Button>
        </>
      ) : (
        <TestProgress gen={gen} onAgain={reset} />
      )}
    </Modal>
  );
}

function TestProgress({ gen, onAgain }: { gen: AdminGenerationDetail | null; onAgain: () => void }) {
  if (!gen) return <Loading />;
  const done = gen.status === 'succeeded' || gen.status === 'failed';
  const step = gen.tasks.filter((t) => t.step === gen.currentStep);
  const fin = step.filter((t) => t.status === 'succeeded' || t.status === 'failed').length;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <Status tone={gen.status === 'succeeded' ? 'ok' : gen.status === 'failed' ? 'bad' : gen.status === 'stuck' ? 'warn' : 'run'}>
          {gen.status === 'succeeded' ? 'Готово' : gen.status === 'failed' ? 'Не прошёл' : gen.status === 'stuck' ? 'Завис' : 'Рисуем'}
        </Status>
        <span className="text-sm text-muted">
          шаг {gen.currentStep + 1} из {gen.totalSteps} · {fin} из {step.length}
        </span>
      </div>
      {!done ? <div className="h-1.5 overflow-hidden rounded-full bg-white/8"><div className="h-full rounded-full bg-accent transition-all" style={{ width: `${step.length ? (fin / step.length) * 100 : 5}%` }} /></div> : null}
      {gen.results.length ? (
        <div className="grid grid-cols-3 gap-2">
          {gen.results.map((r) => (
            <div key={r.url} className={cn('flex flex-col gap-1', r.type === 'audio' && 'col-span-3')}>
              <ResultMedia r={r} />
              {r.label || r.title ? <span className="truncate text-xs text-text2">{r.title || r.label}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
      {gen.status === 'failed' ? <span className="text-sm text-bad">{gen.tasks.find((t) => t.error)?.error ?? gen.error}</span> : null}
      <div className="flex gap-2">
        <Link to={`/generations?id=${gen.id}`} className="flex h-11 flex-1 items-center justify-center rounded-xl border border-line-strong bg-field text-sm font-bold text-white">
          Подробнее
        </Link>
        {done ? (
          <Button className="flex-1" onClick={onAgain}>
            Ещё раз
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function ResultMedia({ r, className }: { r: AdminGenerationDetail['results'][number]; className?: string }) {
  if (r.type === 'video') {
    return <video src={r.url} poster={r.coverUrl ?? undefined} controls playsInline className={cn('aspect-[9/16] w-full rounded-xl bg-black object-cover', className)} />;
  }
  if (r.type === 'audio') {
    return (
      <div className={cn('flex items-center gap-3 rounded-xl border border-line bg-field p-2', className)}>
        {r.coverUrl ? <img src={r.coverUrl} alt="" className="size-12 rounded-lg object-cover" /> : null}
        <audio src={r.url} controls className="h-10 min-w-0 flex-1" />
      </div>
    );
  }
  return (
    <a href={r.url} target="_blank" rel="noreferrer">
      <img src={r.url} alt={r.label ?? ''} className={cn('aspect-[9/16] w-full rounded-xl object-cover', className)} />
    </a>
  );
}
